import { AlertCircleIcon } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { FieldError, Label, TextArea, TextField } from "./ui/field";
import { Note } from "./ui/note";

type ConfirmPromptProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  onConfirm: (reason?: string) => void;
  message: string;
  warningText?: string;
  loading?: boolean;
  danger?: boolean;
  children?: React.ReactNode;
  requireReason?: boolean;
  reasonLabel?: string;
};

export function ConfirmPrompt({
  title = "Confirm",
  loading,
  message,
  onOpenChange,
  onConfirm,
  danger = false,
  warningText,
  open,
  requireReason = false,
  reasonLabel = "Reason",
  ...props
}: ConfirmPromptProps) {
  const [reason, setReason] = React.useState("");
  const [reasonError, setReasonError] = React.useState<string>("");

  const onConfirmInternal = React.useCallback(() => {
    if (requireReason && !reason) {
      setReasonError("Reason is required");
      return;
    }
    onConfirm(reason || undefined);
  }, [onConfirm, requireReason, reason]);

  return (
    <TriggerMaybe
      isOpen={open}
      onOpenChange={onOpenChange}
      triggerChild={props.children}
    >
      <Modal isOpen={open} onOpenChange={onOpenChange}>
        <Dialog>
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center gap-2">
                {danger && (
                  <AlertCircleIcon className="size-4 text-destructive" />
                )}
                {title}
              </div>
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text>{message}</Text>
            {warningText && (
              <Note intent="warning" className="my-0">
                {warningText}
              </Note>
            )}
            {requireReason && (
              <TextField
                value={reason}
                onChange={setReason}
                isRequired
                isInvalid={!!reasonError}
              >
                <Label>{reasonLabel}</Label>
                <TextArea className="resize-none field-sizing-content" />
                <FieldError>{reasonError}</FieldError>
              </TextField>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              onPress={onConfirmInternal}
              isPending={loading}
              type="button"
              variant={danger ? "destructive" : "default"}
            >
              Confirm
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </TriggerMaybe>
  );
}

function TriggerMaybe({
  isOpen,
  children,
  onOpenChange,
  triggerChild,
}: {
  isOpen?: boolean;
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  triggerChild?: React.ReactNode;
}) {
  if (triggerChild) {
    return (
      <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
        {triggerChild}
        {children}
      </DialogTrigger>
    );
  }
  return <>{children}</>;
}
